// Charts + order pipeline for the Admin dashboard.
import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { brand, chartTheme, Card } from "./dashboardShared";
import { useTheme } from "../../providers/ThemeProvider";

// Stage accent colors for the pipeline + bars (concrete hex for inline use)
const STAGE_COLOR = {
  pending: "#b45309",
  sales_executive_assigned: "#1d4ed8",
  design_received: "#0ea5e9",
  graphic_designer_assigned: "#6d28d9",
  quotation_received: "#15803d",
  order_tracking: "#4f46e5",
  order_completed: "#15803d",
};

// Donut palette (works in both themes)
const DONUT_PALETTE = [
  "#4f46e5",
  "#38bdf8",
  "#15803d",
  "#f59e0b",
  "#7c3aed",
  "#1d4ed8",
  "#be123c",
];

export default function Charts({ dash, usersReport, productsReport, part = "all" }) {
  const { theme } = useTheme();
  const ct = useMemo(() => chartTheme(theme), [theme]);

  // CHART: User Registration Trend (Line)
  const userTrendData = useMemo(() => {
    const trends = usersReport?.registrationTrends || [];
    const labels = trends.map((t) => t.month);
    const data = trends.map((t) => t.count);
    return {
      labels,
      datasets: [
        {
          label: "Registrations",
          data,
          fill: true,
          borderWidth: 2.6,
          tension: 0.35,
          borderColor: brand.primary,
          backgroundColor: "rgba(79, 70, 229, 0.14)",
          pointRadius: 3,
          pointBackgroundColor: brand.primary,
          pointBorderColor: "#fff",
          pointBorderWidth: 1.5,
        },
      ],
    };
  }, [usersReport]);

  const userTrendOpts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { mode: "index", intersect: false },
      },
      interaction: { mode: "nearest", axis: "x", intersect: false },
      scales: {
        x: { grid: { display: false }, ticks: { color: ct.tick } },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: ct.tick },
          grid: { color: ct.grid },
        },
      },
    }),
    [ct]
  );

  // Order pipeline rows
  const pipeline = useMemo(() => {
    const stats = dash?.orderStats || {};
    const entries = Object.entries(stats);
    const max = Math.max(1, ...entries.map(([, v]) => Number(v || 0)));
    return entries.map(([k, v]) => ({
      key: k,
      label: k.replaceAll("_", " "),
      value: Number(v || 0),
      pct: Math.round((Number(v || 0) / max) * 100),
      color: STAGE_COLOR[k] || brand.primary,
    }));
  }, [dash]);

  // Products by category (CSS donut, dynamic)
  const categories = useMemo(() => {
    return (productsReport?.categoryTrends || []).map((r, i) => ({
      name: r.category,
      count: Number(r.count || 0),
      color: DONUT_PALETTE[i % DONUT_PALETTE.length],
    }));
  }, [productsReport]);

  const conic = useMemo(() => {
    const total = categories.reduce((a, c) => a + c.count, 0);
    if (!total) return "conic-gradient(var(--color-surface-2) 0 100%)";
    let acc = 0;
    const stops = categories
      .map((c) => {
        const start = (acc / total) * 100;
        acc += c.count;
        const end = (acc / total) * 100;
        return `${c.color} ${start}% ${end}%`;
      })
      .join(", ");
    return `conic-gradient(${stops})`;
  }, [categories]);

  // Orders by status (CSS bars, dynamic)
  const bars = useMemo(() => {
    const entries = Object.entries(dash?.orderStats || {});
    const max = Math.max(1, ...entries.map(([, v]) => Number(v || 0)));
    return entries.map(([k, v]) => {
      const value = Number(v || 0);
      return {
        key: k,
        label: k.replaceAll("_", " "),
        value,
        height: Math.round((value / max) * 130) + 4,
        color: STAGE_COLOR[k] || brand.primary,
      };
    });
  }, [dash]);

  const showTop = part === "all" || part === "top";
  const showBottom = part === "all" || part === "bottom";

  return (
    <>
      {/* Trend + pipeline */}
      {showTop && (
        <section className="section">
          <div className="grid grid--3-1">
            <Card
              title="User Registration Trend"
              sub="Sign-ups across recent months"
              className="card--stretch"
            >
              <div className="chart-h">
                <Line data={userTrendData} options={userTrendOpts} />
              </div>
            </Card>

            <Card title="Order Pipeline" sub="RFQ → Dispatch workflow">
              <div className="pipeline">
                {pipeline.length === 0 && (
                  <div className="empty" style={{ padding: "20px 0" }}>
                    No orders yet
                  </div>
                )}
                {pipeline.map((s) => (
                  <div className="pipeline__row" key={s.key}>
                    <span
                      className="pipeline__dot"
                      style={{ background: s.color }}
                    />
                    <span className="pipeline__label">{s.label}</span>
                    <span className="pipeline__track">
                      <span
                        className="pipeline__fill"
                        style={{ width: `${s.pct}%`, background: s.color }}
                      />
                    </span>
                    <span className="pipeline__val">{s.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Donut + orders-by-status */}
      {showBottom && (
        <section className="section section--bottom">
          <div className="grid grid--1-2">
            <Card title="Products by Category" sub="Catalog distribution">
              <div className="donut-wrap">
                <div className="css-donut" style={{ background: conic }}>
                  <div className="css-donut__hole">
                    <span className="num">{categories.length}</span>
                    <span className="lbl">categories</span>
                  </div>
                </div>
                <div className="donut-legend">
                  {categories.length === 0 && (
                    <div className="empty">No categories</div>
                  )}
                  {categories.map((c) => (
                    <div className="donut-legend__row" key={c.name}>
                      <i style={{ background: c.color }} />
                      <span className="donut-legend__label">{c.name}</span>
                      <span className="donut-legend__val">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card
              title="Orders by Status"
              sub="Current distribution across stages"
              className="card--stretch"
            >
              <div className="bars">
                {bars.length === 0 && <div className="empty">No orders</div>}
                {bars.map((b) => (
                  <div className="bars__col" key={b.key}>
                    <span className="bars__val" style={{ color: b.color }}>
                      {b.value}
                    </span>
                    <div
                      className="bars__bar"
                      style={{ height: `${b.height}px`, background: b.color }}
                    />
                    <span className="bars__label">{b.label}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>
      )}
    </>
  );
}
